/**
 * InsightPro™ — Group P&L  (Multi-Entity Finance clients only)
 *
 * Top-of-house P&L with brand selector + period selector.
 * Drill: Group → Brand → Entity (table) → GL detail (panel)
 *
 * Data: data-api endpoints getFinancePeriods / getFinanceBrands /
 *       getGroupPL / getEntityPLSummary / getEntityPLDetail.
 *       All amounts come from FactGLMonthly.display_amount which is
 *       already sign-normalised (Revenue +, COS/Expense -).
 */
import React, { useState, useEffect, useMemo } from 'react';
import {
  fetchFinancePeriods, fetchFinanceBrands, fetchGroupPL,
  fetchEntityPLSummary, fetchEntityPLDetail,
} from '../utils/api';

// ── Number formatting ────────────────────────────────────────
const fmtMoney = (v) => {
  if (v == null || isNaN(v)) return '—';
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return (v / 1_000_000).toFixed(2) + 'M';
  if (abs >= 1_000)     return (v / 1_000).toFixed(0) + 'K';
  return v.toFixed(0);
};
const fmtPct = (v) => (v == null || isNaN(v)) ? '—' : `${(v * 100).toFixed(1)}%`;
const fmtFull = (v) => (v == null || isNaN(v)) ? '—' :
  v.toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

// ── Styles (mirrors existing pages) ──────────────────────────
const S = {
  page:        { padding: '20px 28px', maxWidth: 1400 },
  header:      { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 20 },
  title:       { fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--page-text)', letterSpacing: '-0.02em' },
  sub:         { fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--page-muted)', marginTop: 4, letterSpacing: '0.1em', textTransform: 'uppercase' },
  selectorBar: { display: 'flex', gap: 12, alignItems: 'flex-end' },
  label:       { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 5 },
  select:      { background: 'var(--page)', border: '1px solid var(--card-border)', padding: '8px 12px', fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--page-text)', outline: 'none', borderRadius: 2, minWidth: 140 },
  kpiGrid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 24 },
  kpiCard:     { background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 4, padding: '16px 20px' },
  kpiLabel:    { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 8 },
  kpiVal:      { fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 700, color: 'var(--page-text)', lineHeight: 1 },
  kpiSub:      { fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)', marginTop: 6 },
  card:        { background: 'var(--card)', border: '1px solid var(--card-border)', borderRadius: 4, padding: '16px 20px', marginBottom: 16 },
  cardTitle:   { fontFamily: 'var(--font-mono)', fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'var(--page-muted)', marginBottom: 12 },
  table:       { width: '100%', borderCollapse: 'collapse' },
  th:          { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--page-muted)', padding: '8px 10px', textAlign: 'left', borderBottom: '1px solid var(--card-border)', background: 'var(--page)' },
  thRight:     { textAlign: 'right' },
  td:          { padding: '9px 10px', fontSize: 12.5, color: 'var(--page-text)', borderBottom: '1px solid var(--card-border)', verticalAlign: 'middle' },
  tdRight:     { textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12 },
  tdMono:      { fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)' },
  rowClick:    { cursor: 'pointer' },
  errorBox:    { padding: 14, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--red)', background: 'rgba(176,40,40,0.08)', border: '1px solid rgba(176,40,40,0.2)', borderRadius: 2, marginBottom: 16 },
  loadingBox:  { padding: 28, textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--page-muted)' },
  brandPill:   (brand) => ({
    fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.1em', textTransform: 'uppercase',
    padding: '2px 8px', borderRadius: 2, display: 'inline-block',
    background: brand === 'Pedros' ? 'rgba(37,99,235,0.10)' : brand === 'Wackys' ? 'rgba(200,149,32,0.10)' : 'rgba(26,173,99,0.10)',
    color:      brand === 'Pedros' ? '#2563eb'              : brand === 'Wackys' ? 'var(--amber)'           : 'var(--green-l)',
  }),
  drilldown:   { background: 'var(--page)', border: '1px solid var(--card-border)', borderRadius: 4, padding: 16, marginTop: 12 },
  closeBtn:    { fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '0.12em', textTransform: 'uppercase', background: 'transparent', border: '1px solid var(--card-border)', padding: '4px 10px', cursor: 'pointer', color: 'var(--page-muted)', borderRadius: 2 },
};

const COLOR_FOR = (v) => v == null ? 'var(--page-muted)' : v >= 0 ? 'var(--green-l)' : 'var(--red)';

// ─────────────────────────────────────────────────────────────
// ── Group-by dimension config ────────────────────────────────
// Each entry defines the column header + how to extract a row's display label
// + which filter key the row's value populates when the user clicks to drill.
const GROUP_BY_OPTIONS = [
  { key: 'country',     label: 'Country',  rowLabel: r => r.country  || '—', filterKey: 'country' },
  { key: 'province',    label: 'Province', rowLabel: r => r.province || (r.country !== 'ZA' ? `(${r.country})` : '—'), filterKey: 'province' },
  { key: 'brand',       label: 'Brand',    rowLabel: r => r.brand    || '—', filterKey: 'brand' },
  { key: 'entity_type', label: 'Type',     rowLabel: r => r.entityType || '—', filterKey: 'entityType' },
  { key: 'entity',      label: 'Entity',   rowLabel: r => r.legalName,        filterKey: null }, // entity rows drill to GL detail
];
const GROUP_BY_BY_KEY = Object.fromEntries(GROUP_BY_OPTIONS.map(o => [o.key, o]));

export default function GroupPL() {
  const [periods, setPeriods]   = useState([]);
  const [period,  setPeriod]    = useState(null);
  const [brands,  setBrands]    = useState({ brands: ['Consolidated'], canPickBrand: false });
  const [brand,   setBrand]     = useState('Consolidated');
  const [pl,      setPl]        = useState(null);
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [err,     setErr]       = useState(null);
  const [drillEntity, setDrillEntity] = useState(null);
  const [drillData,   setDrillData]   = useState(null);

  // Group-by + active filters (from drill clicks)
  const [groupBy, setGroupBy] = useState('entity');           // default to flat entity list
  const [filters, setFilters] = useState({});                 // { country?, province?, entityType? }

  // Load periods + brands once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, b] = await Promise.all([fetchFinancePeriods(), fetchFinanceBrands()]);
        if (cancelled) return;
        setPeriods(p.periods || []);
        setPeriod(p.latest || (p.periods?.[0]?.periodKey ?? null));
        setBrands(b);
        if (b.canPickBrand) setBrand('Consolidated');
        else setBrand(b.brands[0] || 'Consolidated');
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.error || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load PL data when period / brand / groupBy / filters change
  useEffect(() => {
    if (!period) return;
    let cancelled = false;
    setLoading(true); setErr(null); setDrillEntity(null); setDrillData(null);
    const apiBrand = brand === 'Consolidated' ? 'all' : brand;
    Promise.all([
      fetchGroupPL(period, apiBrand, filters),
      fetchEntityPLSummary(period, apiBrand, { groupBy, ...filters }),
    ])
      .then(([gpl, sum]) => {
        if (cancelled) return;
        setPl(gpl);
        setSummary(sum);
        setLoading(false);
      })
      .catch(e => {
        if (cancelled) return;
        setErr(e.response?.data?.error || e.message);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [period, brand, groupBy, filters]);

  // Drilldown: clicking a non-entity row narrows by that dim and switches
  // groupBy to 'entity'. Clicking an entity row opens GL detail.
  const handleRowClick = async (row) => {
    if (groupBy === 'entity') {
      setDrillEntity(row);
      setDrillData(null);
      try {
        const d = await fetchEntityPLDetail(period, row.entityKey);
        setDrillData(d);
      } catch (e) {
        setDrillData({ error: e.response?.data?.error || e.message });
      }
      return;
    }
    // Brand has its own top-level selector — drive that rather than a filter.
    if (groupBy === 'brand') {
      if (row.brand) setBrand(row.brand);
      setGroupBy('entity');
      return;
    }
    // country / province / entity_type → add to filters, switch to entity rows
    const opt = GROUP_BY_BY_KEY[groupBy];
    if (!opt?.filterKey) return;
    const v = row[opt.filterKey === 'entityType' ? 'entityType' : opt.filterKey];
    if (!v) return;
    setFilters(f => ({ ...f, [opt.filterKey]: v }));
    setGroupBy('entity');
  };

  const removeFilter = (k) => setFilters(f => { const out = { ...f }; delete out[k]; return out; });
  const clearFilters = () => { setFilters({}); setGroupBy('entity'); };

  // Period dropdown options sorted desc (latest first)
  const periodOptions = useMemo(() =>
    [...periods].sort((a, b) => (b.periodKey || '').localeCompare(a.periodKey || '')),
    [periods]
  );

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Group P&amp;L</div>
          <div style={S.sub}>
            {brand === 'Consolidated' ? 'Consolidated · all brands' : `${brand} only`}
            {' · '}{period || '—'}
            {filters.country  && ` · ${filters.country}`}
            {filters.province && ` · ${filters.province}`}
            {filters.entityType && ` · ${filters.entityType}`}
          </div>
        </div>
        <div style={S.selectorBar}>
          {brands.canPickBrand && (
            <div>
              <div style={S.label}>Brand</div>
              <select style={S.select} value={brand} onChange={e => setBrand(e.target.value)}>
                {brands.brands.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          )}
          <div>
            <div style={S.label}>Period</div>
            <select style={S.select} value={period || ''} onChange={e => setPeriod(e.target.value)}>
              {periodOptions.map(p => (
                <option key={p.periodKey} value={p.periodKey}>{p.periodKey}{p.monthName ? ` · ${p.monthName}` : ''}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {err && <div style={S.errorBox}>⚠ {err}</div>}

      {loading && <div style={S.loadingBox}>Loading…</div>}

      {!loading && pl && (
        <>
          {/* KPI tiles */}
          <div style={S.kpiGrid}>
            <KpiTile label="Sales"        value={fmtMoney(pl.sales)}        sub={fmtFull(pl.sales)} />
            <KpiTile label="Gross Profit" value={fmtMoney(pl.grossProfit)}  sub={fmtPct(pl.grossProfitPct)} />
            <KpiTile label="Other Income" value={fmtMoney(pl.otherIncome)}  sub={fmtFull(pl.otherIncome)} />
            <KpiTile label="Operating Expense" value={fmtMoney(pl.otherExpense)} sub={fmtFull(pl.otherExpense)} />
            <KpiTile label="EBITDA"       value={fmtMoney(pl.ebitda)}       sub={fmtPct(pl.ebitdaPct)}
                     valueColor={COLOR_FOR(pl.ebitda)} />
          </div>

          {/* Group-by + active filter chips */}
          <div style={{
            display:'flex', alignItems:'center', gap:10, flexWrap:'wrap',
            marginBottom:12, fontSize:12,
          }}>
            <div style={{...S.label, marginBottom:0}}>Group by</div>
            <div style={{ display:'flex', gap:4, border:'1px solid var(--card-border)', borderRadius:2 }}>
              {GROUP_BY_OPTIONS.map(o => (
                <button
                  key={o.key}
                  onClick={() => setGroupBy(o.key)}
                  style={{
                    fontFamily:'var(--font-mono)', fontSize:10, letterSpacing:'0.1em',
                    textTransform:'uppercase', padding:'7px 14px', cursor:'pointer',
                    background: groupBy === o.key ? 'var(--gold)' : 'transparent',
                    color:      groupBy === o.key ? 'var(--void)' : 'var(--page-muted)',
                    border:'none',
                  }}
                >{o.label}</button>
              ))}
            </div>
            {Object.entries(filters).filter(([,v]) => v).map(([k, v]) => (
              <span key={k} style={{
                display:'inline-flex', alignItems:'center', gap:6,
                padding:'4px 10px', background:'rgba(200,149,32,0.10)',
                border:'1px solid rgba(200,149,32,0.25)', borderRadius:2,
                fontFamily:'var(--font-mono)', fontSize:10, color:'var(--amber)',
              }}>
                {k}: {v}
                <button
                  onClick={() => removeFilter(k)}
                  style={{ background:'transparent', border:'none', color:'var(--amber)', cursor:'pointer', padding:0, fontSize:13, lineHeight:1 }}
                >×</button>
              </span>
            ))}
            {Object.values(filters).some(Boolean) && (
              <button onClick={clearFilters} style={{
                background:'transparent', border:'none', color:'var(--page-muted)',
                cursor:'pointer', fontFamily:'var(--font-mono)', fontSize:10,
                letterSpacing:'0.1em', textTransform:'uppercase',
              }}>Clear all</button>
            )}
          </div>

          {/* Aggregated / entity table — same columns either way */}
          <div style={S.card}>
            <div style={S.cardTitle}>
              {groupBy === 'entity'
                ? 'Entities — click a row to drill into GL lines'
                : `By ${GROUP_BY_BY_KEY[groupBy]?.label || groupBy} — click a row to drill into entities`}
            </div>
            {!summary || summary.entities.length === 0 ? (
              <div style={S.loadingBox}>No data for this period / brand / filter combination.</div>
            ) : (
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}>{GROUP_BY_BY_KEY[groupBy]?.label || 'Group'}</th>
                    {groupBy === 'entity' && <th style={S.th}>Brand</th>}
                    {groupBy === 'entity' && <th style={S.th}>Type</th>}
                    <th style={{...S.th, ...S.thRight}}>Sales</th>
                    <th style={{...S.th, ...S.thRight}}>GP</th>
                    <th style={{...S.th, ...S.thRight}}>GP%</th>
                    <th style={{...S.th, ...S.thRight}}>Op. Exp.</th>
                    <th style={{...S.th, ...S.thRight}}>EBITDA</th>
                    <th style={{...S.th, ...S.thRight}}>EBITDA%</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.entities.map((e, i) => {
                    const opt = GROUP_BY_BY_KEY[groupBy];
                    const labelFn = opt?.rowLabel || (r => r.legalName || '—');
                    const rowKey = e.entityKey || `${e.country || ''}|${e.province || ''}|${e.brand || ''}|${e.entityType || ''}|${i}`;
                    return (
                      <tr key={rowKey} style={S.rowClick} onClick={() => handleRowClick(e)}>
                        <td style={S.td}>{labelFn(e)}</td>
                        {groupBy === 'entity' && <td style={S.td}><span style={S.brandPill(e.brand)}>{e.brand}</span></td>}
                        {groupBy === 'entity' && <td style={{...S.td, ...S.tdMono}}>{e.entityType}</td>}
                        <td style={{...S.td, ...S.tdRight}}>{fmtMoney(e.sales)}</td>
                        <td style={{...S.td, ...S.tdRight}}>{fmtMoney(e.grossProfit)}</td>
                        <td style={{...S.td, ...S.tdRight, color: e.grossProfitPct >= 0.15 ? 'var(--green-l)' : e.grossProfitPct >= 0 ? 'var(--page-text)' : 'var(--red)'}}>{fmtPct(e.grossProfitPct)}</td>
                        <td style={{...S.td, ...S.tdRight}}>{fmtMoney(e.otherExpense)}</td>
                        <td style={{...S.td, ...S.tdRight, color: COLOR_FOR(e.ebitda)}}>{fmtMoney(e.ebitda)}</td>
                        <td style={{...S.td, ...S.tdRight, color: COLOR_FOR(e.ebitda)}}>{fmtPct(e.ebitdaPct)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Drilldown panel */}
          {drillEntity && (
            <div style={S.card}>
              <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12}}>
                <div>
                  <div style={S.cardTitle}>GL Lines — {drillEntity.legalName}</div>
                  <div style={{fontFamily:'var(--font-mono)', fontSize:11, color:'var(--page-muted)'}}>
                    {drillEntity.brand} · {drillEntity.entityType} · {drillEntity.country} · {drillEntity.currency}
                  </div>
                </div>
                <button style={S.closeBtn} onClick={() => { setDrillEntity(null); setDrillData(null); }}>✕ Close</button>
              </div>
              {!drillData ? (
                <div style={S.loadingBox}>Loading lines…</div>
              ) : drillData.error ? (
                <div style={S.errorBox}>⚠ {drillData.error}</div>
              ) : drillData.lines.length === 0 ? (
                <div style={S.loadingBox}>No GL lines for this entity / period.</div>
              ) : (
                <table style={S.table}>
                  <thead>
                    <tr>
                      <th style={S.th}>Account Type</th>
                      <th style={S.th}>GL</th>
                      <th style={S.th}>Description</th>
                      <th style={S.th}>Category</th>
                      <th style={{...S.th, ...S.thRight}}>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drillData.lines.map((l, i) => (
                      <tr key={i}>
                        <td style={{...S.td, ...S.tdMono}}>{l.accountType}</td>
                        <td style={{...S.td, ...S.tdMono}}>{l.glAccount}</td>
                        <td style={S.td}>{l.glDescription}</td>
                        <td style={{...S.td, ...S.tdMono}}>{l.reportCategory || '—'}</td>
                        <td style={{...S.td, ...S.tdRight, color: COLOR_FOR(l.amount)}}>{fmtMoney(l.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function KpiTile({ label, value, sub, valueColor }) {
  return (
    <div style={S.kpiCard}>
      <div style={S.kpiLabel}>{label}</div>
      <div style={{ ...S.kpiVal, ...(valueColor ? { color: valueColor } : {}) }}>{value}</div>
      {sub && <div style={S.kpiSub}>{sub}</div>}
    </div>
  );
}
